import { Marker, Map } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "config/store";
import { MapBoundsType } from "globals/types/globals.ts";
import { EventType } from "globals/types/models.ts";
import { searchActions } from "config/store/modules/search.ts";
import { useDebounce, useMount } from "react-use";
import { authActions } from "config/store/modules/auth.ts";
import { getUrlFromFormat } from "globals/functions/image.ts";
import Supercluster from "supercluster";
import { getExtendedBounds, OSMStyle } from "utils/data/map.ts";
import markerPng from "assets/images/marker.png";
import { LANGS } from "globals/constants.ts";
import clamp from "lodash-es/clamp";
import get from "lodash-es/get";
import { getLatitude, getLongitude } from "utils/global.ts";

const DEBUG: boolean = false as const;

const superClusterRadius = typeof window !== "undefined" ? clamp(window.innerWidth * 0.1, 50, 100) : 100;
// eslint-disable-next-line no-console
if (DEBUG) console.log("radius", superClusterRadius);

const superClusterInstance = new Supercluster({
  //use 10% of the screen width as radius
  //in pixels, 500width = 50, 1000width = 100
  radius: superClusterRadius,
  maxZoom: 13,
  minZoom: 0,
  minPoints: 3
});

function MapLibreLayout() {
  const dispatch = useAppDispatch();
  const selectedCategoryId: number | null = useAppSelector(state => state.search.selectedCategoryId);
  const selectedDay: string | null = useAppSelector(state => state.search.selectedDay);
  const userLocation = useAppSelector(state => state.search.userLocation);
  const mapBounds: MapBoundsType = useAppSelector(state => state.search.mapBounds);
  const eventById: { [key: number]: EventType } = useAppSelector(state => state.search.eventById);
  const searchEventIds: number[] = useAppSelector(state => state.search.searchEventIds);
  const currentLang = useAppSelector(state => state.global.currentLang);
  const userLocationMarkerRef = useRef<Marker | null>(null);

  const [userLocationCenterWasInit, setUserLocationCenterWasInit] = useState<boolean>(false);
  const markersRef = useRef<{
    [key: number]: { marker: Marker, visible: boolean, eventUuid: string, eventId: number, alwaysShow: boolean }
  }>({});
  const clustersRefs = useRef<{
    [key: number]: { marker: Marker, visible: boolean, clusterId: number }
  }>({});
  const mapRef = useRef<Map | null>(null);
  const mapDivRef = useRef<HTMLDivElement | null>(null);

  const onMapMoveEnd = useCallback(() => {
    const bounds = mapRef.current?.getBounds();
    if (!bounds) return;

    const extendedBounds = getExtendedBounds({
      westLng: bounds.getWest(),
      southLat: bounds.getSouth(),
      eastLng: bounds.getEast(),
      northLat: bounds.getNorth()
    });

    dispatch(searchActions.setMapBounds(extendedBounds));
  }, [dispatch]);

  const onClickMarker = useCallback((event: EventType) => {
    dispatch(searchActions.setSelectedEvent(event));
    dispatch(authActions.statCollectionEventByUuid(event.uuid, "click", "map_marker"));
  }, [dispatch]);

  const [_, cancelDebounceSearchEvents] = useDebounce(() => searchEvents(), 100, [selectedDay, selectedCategoryId, mapBounds]);

  const searchEvents = useCallback(() => {
    if (!mapBounds.northLat) return;

    cancelDebounceSearchEvents();
    dispatch(searchActions.searchEvents());

    if (DEBUG) {
      // Debugging code for drawing bounds on the map
    }
  }, [mapBounds, cancelDebounceSearchEvents, dispatch]);

  useEffect(() => {
    if (!mapRef.current) {
      return;
    }

    //loop object of all event
    for (const eventId in eventById) {
      const numberEventId = parseInt(eventId);
      const isInSearch = searchEventIds.includes(numberEventId);

      if (!isInSearch) {
        const exist = markersRef.current[numberEventId] || null;

        //hide it
        if (exist) {
          //skip if it should always be visible
          if (markersRef.current[numberEventId].alwaysShow) {
            //  continue;
          }

          const isVisible = exist.visible;

          //hide marker only if visible
          if (isVisible) {
            markersRef.current[numberEventId].visible = false;
            markersRef.current[numberEventId].marker.setOpacity("0");
            markersRef.current[numberEventId].marker.addClassName("-hidden");
            // eslint-disable-next-line no-console
            if (DEBUG) console.log("hiding ", numberEventId);
          }
        }
      }
    }

    //points for cluster
    const points = [];
    //convert searchEventIds to object for faster code later
    const searchEventIdsObj: { [key: number]: boolean } = {};

    for (const eventId of searchEventIds) {
      const event = eventById[eventId];

      const eventLat = getLatitude(event.latitude);
      const eventLng = getLongitude(event.longitude);

      if (!eventLat || !eventLng) {
        continue;
      }

      //add to map
      searchEventIdsObj[event.id] = true;

      const alwaysShow = get(event.options, "size", "normal") === "large";

      //add to cluster
      points.push({
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [eventLng, eventLat]
        },
        properties: {
          cluster: false,
          alwaysShow: alwaysShow,
          eventId: event.id
        }
      });

      const exist = markersRef.current[event.id] ?? null;

      if (exist) {
        //perfect dont create it again, cluster will make it visible if needed
        continue;
      }

      const eventImageUrl = event.images.length > 0 ? getUrlFromFormat(event.images[0].imageUrl, event.images[0].formats, 124) : "";

      let eventTitle = currentLang === LANGS.FR ? event.titleFr : event.titleEn ? event.titleEn : event.titleFr;
      if (eventTitle && eventTitle.length > 40) {
        eventTitle = `${eventTitle.substring(0, 40)}...`;
      }
      const markerElement = document.createElement("div");
      markerElement.className = `g-map-icon`;
      markerElement.innerHTML = `<div class="g-map-icon__container ${event.options?.size === "large" ? "-large" : ""}"><div class="g-map-icon__img">${eventImageUrl ? `<img src="${eventImageUrl}" />` : ""}</div><div class="g-map-icon__text"><span>${eventTitle}</span></div></div>`;

      //dont add the marker to the map yet, we will do it in the cluster
      const marker = new Marker({
        element: markerElement,
        anchor: "bottom",
        offset: [0, -10]//account for the arrow which is 10px in height
      })
        .setLngLat([eventLng, eventLat])
        .setOpacity("0")
        .addTo(mapRef.current);


      if (event.options?.size === "large") {
        marker.addClassName("-large");
      }
      marker.addClassName("-hidden");//start hidden

      markerElement.addEventListener("click", () => onClickMarker(event));


      markersRef.current[event.id] = {
        marker: marker,
        alwaysShow: alwaysShow,
        eventId: event.id,
        eventUuid: event.uuid,
        visible: false//cluster will make it visible
      };
      // eslint-disable-next-line no-console
      if (DEBUG) console.log("created event marker ", event.id);
    }


    //add points to cluster so it can create clusters
    //@ts-expect-error supercluster dont like our points not being typed
    superClusterInstance.load(points);

    //create clusters from current map settings
    const bounds = mapRef.current.getBounds();
    const extendedBounds = getExtendedBounds({
      westLng: bounds.getWest(),
      southLat: bounds.getSouth(),
      eastLng: bounds.getEast(),
      northLat: bounds.getNorth()
    });
    const zoom = mapRef.current.getZoom();

    const clusters = superClusterInstance.getClusters(
      [
        extendedBounds.westLng as number,
        extendedBounds.southLat as number,
        extendedBounds.eastLng as number,
        extendedBounds.northLat as number
      ],
      Math.floor(zoom));

    //copy search event ids to hide map, id in this map need to be hidden
    const toHideEventIds = {
      ...searchEventIdsObj
    };

    const toHideMarkerClusterIds: { [key: number]: boolean } = Object.keys(clustersRefs.current).reduce((acc: {
      [key: number]: boolean
    }, key) => {
      acc[parseInt(key)] = true;
      return acc;
    }, {});

    for (const cluster of clusters) {
      const [longitude, latitude] = cluster.geometry.coordinates;

      const clusterLat = getLatitude(latitude);
      const clusterLng = getLongitude(longitude);

      if (!clusterLat || !clusterLng) {
        continue;
      }

      const properties = cluster.properties;

      //this element is a cluster, so create or make it visible it
      if (properties.cluster) {
        const clusterId = properties.cluster_id;
        const exist = clustersRefs.current[clusterId] ?? null;
        if (!exist) {
          let clusterSize: "-small" | "-medium" | "-big" = "-small";
          if (properties.point_count > 25) {
            clusterSize = "-big";
          } else if (properties.point_count > 10) {
            clusterSize = "-medium";
          }


          const clusterElement = document.createElement("div");
          clusterElement.className = `marker-cluster ${clusterSize}`;
          clusterElement.innerHTML = `<div><span>${properties.point_count_abbreviated}</span></div>`;

          const clusterMarker = new Marker({ element: clusterElement })
            .setLngLat([clusterLng, clusterLat])
            .setOpacity("0")
            .addTo(mapRef.current);

          clusterMarker.addClassName("-hidden");//start hidden

          clusterElement.addEventListener("click", () => {
            if (!mapRef.current) return;
            const zoomOfExpansion = superClusterInstance.getClusterExpansionZoom(clusterId);
            if (zoomOfExpansion) {
              mapRef.current.easeTo({
                center: [clusterLng, clusterLat],
                zoom: zoomOfExpansion
              });
              return;
            }

            //just zoom by one
            const zoom = mapRef.current?.getZoom();
            const maxZoom = mapRef.current?.getMaxZoom();
            if (zoom === maxZoom) return;//max zoom
            mapRef.current?.easeTo({
              center: [clusterLng, clusterLat],
              zoom: zoom + 1
            });

          });

          clustersRefs.current[clusterId] = {
            marker: clusterMarker,
            clusterId: clusterId,
            visible: false//cluster will make it visible
          };
          // eslint-disable-next-line no-console
          if (DEBUG) console.log("created cluster ", clusterId);
        }

        if (!clustersRefs.current[clusterId].visible) {
          clustersRefs.current[clusterId].visible = true;
          clustersRefs.current[clusterId].marker.setOpacity("1");
          clustersRefs.current[clusterId].marker.removeClassName("-hidden");

          // eslint-disable-next-line no-console
          if (DEBUG) console.log("set cluster visible ", clusterId);
        }

        delete toHideMarkerClusterIds[clusterId];//remove from hide array so we dont hide later on

        continue;
      }


      //this is en event
      //should be visible as event
      const event = eventById[properties.eventId];
      delete toHideEventIds[event.id];//remove from hide array so we dont hide later on


      //if not visible yet or if it should always be visible
      if (!markersRef.current[event.id].visible || markersRef.current[event.id].alwaysShow) {
        markersRef.current[event.id].visible = true;
        markersRef.current[event.id].marker.setOpacity("1");
        markersRef.current[event.id].marker.removeClassName("-hidden");
      }

      // eslint-disable-next-line no-console
      if (DEBUG) console.log("set event visible ", event.id);

    }

    //hide all event that are not in the cluster loop, that mean a cluster hide them
    for (const clusterId in toHideMarkerClusterIds) {
      //if visible
      if (clustersRefs.current[clusterId].visible) {
        clustersRefs.current[clusterId].visible = false;
        clustersRefs.current[clusterId].marker.setOpacity("0");
        clustersRefs.current[clusterId].marker.addClassName("-hidden");
        // eslint-disable-next-line no-console
        if (DEBUG) console.log("hide cluster ", clusterId);
      }
    }


    //hide all event that are not in the cluster loop, that mean a cluster hide them
    for (const eventId in toHideEventIds) {
      const event = eventById[parseInt(eventId)];


      //make sure to show it
      if (markersRef.current[event.id].alwaysShow) {
        markersRef.current[event.id].visible = true;
        markersRef.current[event.id].marker.setOpacity("1");
        markersRef.current[event.id].marker.removeClassName("-hidden");
        // eslint-disable-next-line no-console
        if (DEBUG) console.log("hide event ", event.id);
        continue;
      }

      //if visible
      if (markersRef.current[event.id].visible) {
        markersRef.current[event.id].visible = false;
        markersRef.current[event.id].marker.setOpacity("0");
        markersRef.current[event.id].marker.addClassName("-hidden");
        // eslint-disable-next-line no-console
        if (DEBUG) console.log("hide event ", event.id);
      }

    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchEventIds]);

  useEffect(() => {
    if (!userLocation) return;
    if (!mapRef.current) return;
    const userLat = getLatitude(userLocation.lat);
    const userLng = getLongitude(userLocation.lng);

    if (!userLng || !userLat) {
      //if no data, remove marker if exist
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.remove();
        userLocationMarkerRef.current = null;
      }
      return;
    }

    //if exist, simply move it
    if (userLocationMarkerRef.current) {
      userLocationMarkerRef.current?.setLngLat([userLng, userLat]);
      return;
    }

    //make sure we dont init to the center of user location each time it change
    if (!userLocationCenterWasInit) {
      mapRef.current?.setCenter([userLng, userLat]);
      mapRef.current?.setZoom(10);
      setUserLocationCenterWasInit(true);
    }


    const markerElement = document.createElement("div");
    markerElement.className = "g-icon-marker";
    markerElement.innerHTML = `<img class="block min-w-[60px] w-[60px] h-[60px]" src="${markerPng}" alt="marker"/>`;

    userLocationMarkerRef.current = new Marker({
      element: markerElement,
      anchor: "center"
    })
      .setLngLat([userLng, userLat])
      .addTo(mapRef.current);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLocation]);

  useMount(() => {
    if (!mapDivRef.current) {
      return;
    }

    if (mapRef.current) {
      return;
    }

    const defaultZoom = userLocation ? 10 : 8;
    const userLat = userLocation ? getLatitude(userLocation.lat) : null;
    const userLng = userLocation ? getLongitude(userLocation.lng) : null;

    mapRef.current = new Map({
      container: mapDivRef.current,
      attributionControl: false,//todo

      //@ts-expect-error style is weird
      style: OSMStyle,
      center: {
        lat: userLat ? userLat : 46,
        lng: userLng ? userLng : -71
      },
      dragRotate: false,
      touchZoomRotate: true,
      zoom: defaultZoom,
      maxZoom: 18,
      minZoom: 3
    });

    // disable map rotation using right click + drag
    mapRef.current.dragRotate.disable();
    // disable map rotation using keyboard
    mapRef.current.keyboard.disable();
    // disable map rotation using touch rotation gesture
    mapRef.current.touchZoomRotate.disableRotation();

    mapRef.current.on("moveend", onMapMoveEnd);

    mapRef.current.on("load", () => {
      const initBounds = mapRef.current?.getBounds();
      if (!initBounds) return;

      const extendedBounds = getExtendedBounds({
        westLng: initBounds.getWest(),
        southLat: initBounds.getSouth(),
        eastLng: initBounds.getEast(),
        northLat: initBounds.getNorth()
      });
      dispatch(searchActions.setMapBounds(extendedBounds));
    });

    return () => {
      mapRef.current?.remove();
      mapRef.current = null;
    };
  });

  return (
    <div ref={mapDivRef} className="g-map | h-full w-full" />
  );
}

export default MapLibreLayout;
